import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { axiosInstance, useClientInfo } from "../utils/commonImports";

export default function PurchaseRegister() {
  const { clientId } = useClientInfo();

  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [vendor, setVendor] = useState("ALL");

  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState("ALL");

  /* ================= LOAD VENDORS ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/vendors", { params: { client_id: clientId } })
      .then((res) => setVendors(res.data || []))
      .catch(console.error);
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/vehicles", { params: { client_id: clientId } })
      .then((res) => setVehicles(res.data || []))
      .catch(console.error);
  }, [clientId]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/purchases/purchase-register", {
        params: {
          client_id: clientId,
          fromDate: fromDate || null,
          toDate: toDate || null,
          vendor_id: vendor === "ALL" ? null : vendor,
          vehicle_id: vehicle === "ALL" ? null : vehicle,
        },
      })
      .then((res) => setRows(res.data || []))
      .catch(console.error);
  }, [clientId, fromDate, toDate, vendor, vehicle]);

  /* ================= TOTALS ================= */
  const sum = (field) => rows.reduce((t, r) => t + Number(r[field] || 0), 0);

  const totals = {
    basic: sum("basic_amount"),
    escort: sum("escort_amount"),
    batta: sum("batta_amount"),
    parking: sum("parking_amount"),
    others: sum("others_amount"),
    permit: sum("permit_amount"),
    toll: sum("toll_amount"),
    fuel: sum("fuel_esc_amount"),
    add: sum("addition_amount"),
    discount: sum("discount"),
    igst: sum("igst_amount"),
    cgst: sum("cgst_amount"),
    sgst: sum("sgst_amount"),
    net: sum("net_amount"),
    rcm_igst: rows

      .filter((r) => r.gst_treatment?.toLowerCase().includes("rcm"))
      .reduce((t, r) => t + Number(r.igst_amount || 0), 0),

    rcm_cgst: rows
      .filter((r) => r.gst_treatment?.toLowerCase().includes("rcm"))
      .reduce((t, r) => t + Number(r.cgst_amount || 0), 0),

    rcm_sgst: rows
      .filter((r) => r.gst_treatment?.toLowerCase().includes("rcm"))
      .reduce((t, r) => t + Number(r.sgst_amount || 0), 0),
  };

  const num = (v) => Number(v || 0).toFixed(2);

  return (
    <Box p={2}>
      <Typography fontWeight={700} mb={2}>
        Purchase Register
      </Typography>

      {/* FILTER BAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          background: "#fafafa",
          border: "1px solid #ddd",
          borderRadius: 1,
          px: 2,
          py: 1.2,
          mb: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            format="DD/MM/YYYY"
            value={fromDate ? dayjs(fromDate) : null}
            onChange={(newValue) =>
              setFromDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: 160,
                  "& .MuiInputBase-root": {
                    height: 34,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                },
              },
            }}
          />

          <DatePicker
            label="To Date"
            format="DD/MM/YYYY"
            value={toDate ? dayjs(toDate) : null}
            onChange={(newValue) =>
              setToDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: 160,
                  "& .MuiInputBase-root": {
                    height: 34,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                },
              },
            }}
          />
        </LocalizationProvider>

        <FormControl
          size="small"
          sx={{
            width: 240,
            "& .MuiInputBase-root": {
              height: 34,
              fontSize: "0.75rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.75rem",
            },
          }}
        >
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendor}
            label="Vendor"
            onChange={(e) => setVendor(e.target.value)}
          >
            <MenuItem value="ALL">All Vendors</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.vendorid} value={v.vendorid}>
                {v.vendorname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            width: 240,
            "& .MuiInputBase-root": {
              height: 34,
              fontSize: "0.75rem",
            },
          }}
        >
          <InputLabel>Vehicle</InputLabel>
          <Select
            value={vehicle}
            label="Vehicle"
            onChange={(e) => setVehicle(e.target.value)}
          >
            <MenuItem value="ALL">All Vehicles</MenuItem>

            {vehicles.map((vh) => (
              <MenuItem key={vh.vehicle_id} value={vh.vehicle_id}>
                {vh.vehicle_number}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper>
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed", // ⭐ VERY IMPORTANT
              minWidth: 1700, // Adjust if needed
              "& th, & td": {
                border: "1px solid #ccc",
                fontSize: "0.70rem",
                padding: "4px 6px",
                whiteSpace: "nowrap",
              },
            }}
          >
            {/* HEADER */}
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#eeeeee",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                   border: "1px solid #e0e0e0",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell rowSpan={2} sx={{ width: 110 }}>
                  Bill No
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Date
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 220 }}>
                  Party
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Basic
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Escort
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Batta
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Parking
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Others
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Permit
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Toll
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 80 }}>
                  Fuel
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Addition
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Discount
                </TableCell>

                <TableCell colSpan={3} sx={{ width: 150 }}>
                  GST %
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  GST Amount
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  RCM GST
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 110 }}>
                  Net
                </TableCell>
              </TableRow>

              <TableRow
                sx={{
                  "& th": {

                    backgroundColor: "#eeeeee",
                    fontSize: "0.70rem",
                    fontWeight: 700,
                    border: "1px solid #e0e0e0",
                  },
                }}
              >
                <TableCell sx={{ width: 50 }}>IGST%</TableCell>
                <TableCell sx={{ width: 50 }}>CGST%</TableCell>
                <TableCell sx={{ width: 50 }}>SGST%</TableCell>

                <TableCell sx={{ width: 70 }}>IGST</TableCell>
                <TableCell sx={{ width: 70 }}>CGST</TableCell>
                <TableCell sx={{ width: 70 }}>SGST</TableCell>

                <TableCell sx={{ width: 70 }}>IGST</TableCell>
                <TableCell sx={{ width: 70 }}>CGST</TableCell>
                <TableCell sx={{ width: 70 }}>SGST</TableCell>
              </TableRow>
            </TableHead>

            {/* BODY */}
            <TableBody>
              {(vendor === "ALL" && vehicle === "ALL"
                ? Object.entries(
                    rows.reduce((acc, row) => {
                      const key = row.party_name || "Unknown";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(row);
                      return acc;
                    }, {}),
                  )
                : [["DATA", rows]]
              ).map(([groupName, groupRows], idx) => {
                const sub = (field) =>
                  groupRows.reduce((t, r) => t + Number(r[field] || 0), 0);

                return (
                  <React.Fragment key={idx}>
                    {/* GROUP TITLE ONLY WHEN BOTH ALL */}
                    {vendor === "ALL" && vehicle === "ALL" && (
                      <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                        <TableCell
                          colSpan={20}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.80rem",
                            padding: "8px",
                          }}
                        >
                          {groupName}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* DATA ROWS */}
                    {groupRows.map((r, i) => {
                      const isRCM = r.gst_treatment
                        ?.toLowerCase()
                        .includes("rcm");

                      return (
                        <TableRow
                          key={i}
                          sx={{
                            backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
                            "& td": {
                              fontSize: "0.70rem",
                              border: "1px solid #e0e0e0",
                            },
                          }}
                        >
                          <TableCell>{r.purchase_number}</TableCell>
                          <TableCell>
                            {dayjs(r.purchase_date).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell>{r.party_name}</TableCell>

                          <TableCell align="right">
                            {num(r.basic_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.escort_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.batta_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.parking_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.others_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.permit_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.toll_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.fuel_esc_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.addition_amount)}
                          </TableCell>
                          <TableCell align="right">{num(r.discount)}</TableCell>

                          {/* GST % */}
                          <TableCell align="right">
                            {num(r.igst_percent)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.cgst_percent)}
                          </TableCell>
                          <TableCell align="right">
                            {num(r.sgst_percent)}
                          </TableCell>

                          {/* NORMAL GST */}
                          <TableCell align="right">
                            {isRCM ? "0.00" : num(r.igst_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {isRCM ? "0.00" : num(r.cgst_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {isRCM ? "0.00" : num(r.sgst_amount)}
                          </TableCell>

                          {/* RCM GST */}
                          <TableCell align="right">
                            {isRCM ? num(r.igst_amount) : "0.00"}
                          </TableCell>
                          <TableCell align="right">
                            {isRCM ? num(r.cgst_amount) : "0.00"}
                          </TableCell>
                          <TableCell align="right">
                            {isRCM ? num(r.sgst_amount) : "0.00"}
                          </TableCell>

                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {num(r.net_amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* SUBTOTAL WHEN BOTH ALL */}
                    {vendor === "ALL" && vehicle === "ALL" && (
                      <TableRow
                        sx={{
                          backgroundColor: "#f1f2f6",
                          "& td": {
                            fontWeight: 700,
                            borderTop: "2px solid #e0e0e0",
                            borderBottom: "1px solid #e0e0e0",
                          },
                        }}
                      >
                        <TableCell colSpan={3}>Subtotal</TableCell>

                        <TableCell align="right">
                          {num(sub("basic_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("escort_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("batta_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("parking_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("others_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("permit_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("toll_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("fuel_esc_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("addition_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("discount"))}
                        </TableCell>

                        <TableCell colSpan={3}></TableCell>

                        <TableCell align="right">
                          {num(sub("igst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("cgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("sgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_igst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_cgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_sgst_amount"))}
                        </TableCell>

                        <TableCell align="right">
                          {num(sub("net_amount"))}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>

            {/* FOOTER */}
            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& td": {
                    fontWeight: 700,
                    borderTop: "2px solid #bbbaba",
                    borderBottom: "2px solid #e0e0e0",
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableCell colSpan={3}>TOTAL</TableCell>

                <TableCell align="right">{num(totals.basic)}</TableCell>
                <TableCell align="right">{num(totals.escort)}</TableCell>
                <TableCell align="right">{num(totals.batta)}</TableCell>
                <TableCell align="right">{num(totals.parking)}</TableCell>
                <TableCell align="right">{num(totals.others)}</TableCell>
                <TableCell align="right">{num(totals.permit)}</TableCell>
                <TableCell align="right">{num(totals.toll)}</TableCell>
                <TableCell align="right">{num(totals.fuel)}</TableCell>
                <TableCell align="right">{num(totals.add)}</TableCell>
                <TableCell align="right">{num(totals.discount)}</TableCell>

                <TableCell colSpan={3}></TableCell>

                <TableCell align="right">{num(totals.igst)}</TableCell>
                <TableCell align="right">{num(totals.cgst)}</TableCell>
                <TableCell align="right">{num(totals.sgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_igst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_cgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_sgst)}</TableCell>

                <TableCell align="right">{num(totals.net)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
